import React from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileText,
  Gauge,
  Loader2,
  ScanSearch,
  UploadCloud,
  X,
} from "lucide-react";
import { useCheckout } from "@/lib/checkout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AtsChecker = ({ hideHeader = false }) => {
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const { startCheckout, loadingPlan } = useCheckout();

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large", { description: "Please upload a file under 10MB." });
      return;
    }
    setFile(f);
    setResult(null);
  };

  const runCheck = async () => {
    if (!file || loading) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/cv/quick-score`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        status === 429 ? "Daily limit reached" : "Couldn't analyze that file",
        {
          description:
            err?.response?.data?.detail ||
            "Make sure it's a text-based PDF, DOCX, or TXT — not a scanned image.",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (s) => (s >= 70 ? "text-emerald-400" : s >= 45 ? "text-amber-400" : "text-red-400");

  return (
    <section id="free-check" className="relative py-24 sm:py-32" data-testid="ats-section">
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-1/4 h-[400px] w-[700px] rounded-full bg-blue-600/10 blur-[140px] pointer-events-none"
      />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {!hideHeader && (
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Free instant check</p>
            <h2
              className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
              data-testid="ats-headline"
            >
              Is a robot rejecting your CV before a human ever sees it?
            </h2>
            <p className="mt-4 text-zinc-400 text-balance">
              75% of resumes are filtered out by ATS software before reaching a
              recruiter. Drop yours below — get a free, brutally honest score in seconds.
            </p>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="mt-10 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-10"
        >
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-2xl border border-dashed p-8 text-center transition-colors ${
              dragOver ? "border-white/40 bg-white/5" : "border-white/15 bg-black/20 hover:bg-white/5"
            }`}
            data-testid="ats-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="ats-input-file"
            />
            {file ? (
              <div className="flex items-center justify-between gap-3 text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-zinc-300 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{file.name}</p>
                    <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setResult(null);
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-white"
                  data-testid="ats-remove-file"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <UploadCloud className="h-7 w-7 text-zinc-400" />
                <p className="mt-3 text-sm text-zinc-300">
                  Drag & drop your CV or <span className="underline underline-offset-2">browse</span>
                </p>
                <p className="mt-1 text-[11px] text-zinc-500">PDF, DOCX, TXT · free · no account needed</p>
              </div>
            )}
          </div>

          <button
            onClick={runCheck}
            disabled={!file || loading}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
            data-testid="ats-submit"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing…
              </>
            ) : (
              <>
                <ScanSearch className="h-4 w-4" />
                Get my free ATS score
              </>
            )}
          </button>

          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-8"
                data-testid="ats-result"
              >
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="relative shrink-0 flex flex-col items-center justify-center h-32 w-32 rounded-full border border-white/10 bg-black/40">
                    <Gauge className="absolute top-4 h-4 w-4 text-zinc-600" />
                    <span
                      className={`text-4xl font-semibold tracking-tighter ${scoreColor(result.score)}`}
                      data-testid="ats-score"
                    >
                      {result.score}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">/ 100</span>
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-white font-medium leading-snug" data-testid="ats-verdict">
                      {result.headline}
                    </p>
                    <p className="mt-2 text-xs text-zinc-500">
                      Analyzed by the same AI engine that powers our paid rewrites.
                    </p>
                  </div>
                </div>

                {result.issues?.length > 0 && (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-5" data-testid="ats-issues">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                      Biggest problems found
                    </p>
                    <ul className="mt-3 space-y-2.5">
                      {result.issues.map((issue, i) => (
                        <li key={i} className="flex gap-2.5 text-sm text-zinc-300 leading-relaxed">
                          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400 mt-0.5" />
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 text-center">
                  <p className="text-sm text-white font-medium flex items-center justify-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    Our AI fixes all of this — and rewrites the whole document.
                  </p>
                  <button
                    onClick={() => startCheckout("cv_pro")}
                    disabled={loadingPlan === "cv_pro"}
                    className="group mt-4 inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                    data-testid="ats-cta"
                  >
                    {loadingPlan === "cv_pro" && <Loader2 className="h-4 w-4 animate-spin" />}
                    Fix my CV with AI — from $12.13
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
